import React, { useState, useMemo, useEffect } from 'react';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import BrandHeader from '../../components/ui/BrandHeader';
import FilterToolbar from './components/FilterToolbar';
import StudentTableRow from './components/StudentTableRow';
import StudentMobileCard from './components/StudentMobileCard';
import BulkActionsBar from './components/BulkActionsBar';
import EmptyState from './components/EmptyState';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';
import { getAllStudents, bulkImportStudents } from '../../services/studentService';
import { getCurrentAcademicYear } from '../../services/feeService';
import ExcelUploadModal from './components/ExcelUploadModal';
import UploadResultModal from './components/UploadResultModal';
import BulkImageUploadModal from './components/BulkImageUploadModal';
import useRealtimeSubscription from '../../hooks/useRealtimeSubscription';

const StudentListManagement = () => {
  const [filters, setFilters] = useState({
    search: '',
    class: 'all',
    section: 'all',
    academicYear: 'all',
    paymentStatus: 'all'
  });

  const [selectedStudents, setSelectedStudents] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showBulkImageModal, setShowBulkImageModal] = useState(false);

  useEffect(() => {
    loadStudents();
  }, []);

  // Realtime: reload when students or payments change
  useRealtimeSubscription(
    [
      { table: 'students' },
      { table: 'payments' }
    ],
    () => {
      loadStudents();
    },
    []
  );

  const loadStudents = async () => {
    try {
      setLoading(true);
      const currentYear = await getCurrentAcademicYear();
      setCurrentAcademicYear(currentYear);
      
      if (currentYear?.id) {
        const studentsData = await getAllStudents(currentYear?.id);
        setStudents(studentsData);
      }
    } catch (error) {
      console.error('Error loading students:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = useMemo(() => {
    return students?.filter(student => {
      const matchesSearch = filters?.search === '' || 
        student?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        student?.admissionNumber?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      
      const matchesClass = filters?.class === 'all' || student?.class === filters?.class;
      const matchesSection = filters?.section === 'all' || student?.section === filters?.section;
      const matchesStatus = filters?.paymentStatus === 'all' || student?.paymentStatus === filters?.paymentStatus;

      return matchesSearch && matchesClass && matchesSection && matchesStatus;
    });
  }, [filters, students]);

  const sortedStudents = useMemo(() => {
    const sorted = [...filteredStudents];
    sorted?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'outstandingBalance') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredStudents, sortConfig]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      class: 'all',
      section: 'all',
      academicYear: 'all',
      paymentStatus: 'all'
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectStudent = (studentId, isSelected) => {
    setSelectedStudents(prev => 
      isSelected 
        ? [...prev, studentId]
        : prev?.filter(id => id !== studentId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedStudents(isSelected ? sortedStudents?.map(s => s?.id) : []);
  };

  const handleSendReminders = () => {
    alert(`Sending payment reminders to ${selectedStudents?.length} student(s)`);
  };

  const handleGenerateReport = () => {
    alert(`Generating report for ${selectedStudents?.length} student(s)`);
  };

  const handleExport = () => {
    alert('Exporting student list to Excel...');
  };

  const handleBulkUpload = async (studentsData) => {
    try {
      const results = await bulkImportStudents(studentsData, currentAcademicYear?.id);
      setUploadResults(results);
      setShowResultsModal(true);
      
      // Reload students if any were successfully imported
      if (results?.success > 0) {
        await loadStudents();
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadResults({
        success: 0,
        failed: studentsData?.length || 0,
        errors: [{ row: 0, message: 'Failed to process upload. Please try again.' }]
      });
      setShowResultsModal(true);
    }
  };

  const handleCloseResultsModal = () => {
    setShowResultsModal(false);
    setUploadResults(null);
  };

  const allSelected = sortedStudents?.length > 0 && selectedStudents?.length === sortedStudents?.length;
  const someSelected = selectedStudents?.length > 0 && selectedStudents?.length < sortedStudents?.length;

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebarNavigation />
      <div className="lg:ml-64">
        <BrandHeader variant="admin" />
        
        <main className="p-4 md:p-6 lg:p-8 pb-20 lg:pb-8">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
              Student List Management
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Comprehensive student directory with advanced filtering and payment tracking
            </p>
          </div>

          <FilterToolbar
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            resultCount={sortedStudents?.length}
            onExport={handleExport}
            onBulkUpload={() => setShowUploadModal(true)}
            onBulkImageUpload={() => setShowBulkImageModal(true)}
          />

          {loading ? (
            <div className="bg-card rounded-lg border border-border p-8 text-center">
              <p className="text-muted-foreground">Loading students...</p>
            </div>
          ) : sortedStudents?.length === 0 ? (
            <EmptyState onClearFilters={handleClearFilters} />
          ) : (
            <>
              <div className="hidden lg:block bg-card rounded-lg border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left w-12">
                          <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(e) => handleSelectAll(e?.target?.checked)}
                          />
                        </th>
                        <th className="px-4 py-3 text-left">
                          <button
                            onClick={() => handleSort('name')}
                            className="flex items-center gap-2 font-medium text-sm text-foreground hover:text-primary transition-colors"
                          >
                            Student Details
                            <Icon 
                              name={sortConfig?.key === 'name' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                              size={16} 
                            />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left">
                          <button
                            onClick={() => handleSort('class')}
                            className="flex items-center gap-2 font-medium text-sm text-foreground hover:text-primary transition-colors"
                          >
                            Class
                            <Icon 
                              name={sortConfig?.key === 'class' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                              size={16} 
                            />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left">
                          <span className="font-medium text-sm text-foreground">Academic Year</span>
                        </th>
                        <th className="px-4 py-3 text-left">
                          <button
                            onClick={() => handleSort('paymentStatus')}
                            className="flex items-center gap-2 font-medium text-sm text-foreground hover:text-primary transition-colors"
                          >
                            Payment Status
                            <Icon 
                              name={sortConfig?.key === 'paymentStatus' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                              size={16} 
                            />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left">
                          <span className="font-medium text-sm text-foreground">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedStudents?.map(student => (
                        <StudentTableRow
                          key={student?.id}
                          student={student}
                          isSelected={selectedStudents?.includes(student?.id)}
                          onSelect={handleSelectStudent}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="lg:hidden space-y-3">
                {sortedStudents?.map(student => (
                  <StudentMobileCard
                    key={student?.id}
                    student={student}
                    isSelected={selectedStudents?.includes(student?.id)}
                    onSelect={handleSelectStudent}
                  />
                ))}
              </div>
            </>
          )}

          <BulkActionsBar
            selectedCount={selectedStudents?.length}
            onSendReminders={handleSendReminders}
            onGenerateReport={handleGenerateReport}
            onClearSelection={() => setSelectedStudents([])}
          />

          <BulkImageUploadModal
            isOpen={showBulkImageModal}
            onClose={() => setShowBulkImageModal(false)}
            onUploadComplete={loadStudents}
          />

          <ExcelUploadModal
            isOpen={showUploadModal}
            onClose={() => setShowUploadModal(false)}
            onUploadSuccess={handleBulkUpload}
          />

          <UploadResultModal
            isOpen={showResultsModal}
            results={uploadResults}
            onClose={handleCloseResultsModal}
          />
        </main>
      </div>
    </div>
  );
};

export default StudentListManagement;